import { c as _c } from "react/compiler-runtime";
import Chip from '@precis-digital/kurama/Chip';
import SingleSelectDropdown, { type SingleSelectDropdownItem } from '@precis-digital/kurama/SingleSelectDropdown';
import Tooltip, { type TooltipProps } from '@precis-digital/kurama/Tooltip';
import Badge from '@precis-digital/kurama/Badge';
import { type Dispatch, type ReactElement } from 'react';
import type { ChartCardStateAction, ChartDimensionChoice, ChartDimensionValueChoice, ChartDimensionValueOfChoice, ChartMetricChoice, ChartMetricTransposedChoice, ChartMetricVariantsChoice, ChartVariantValueChoice } from '../internalTypes';
import type { ChartChoiceOptions, ChartDataRow, SingleSelectionValue } from '../types';
import { changeIsVisible } from '../utils/changeVisibility';
import { assertNever, getLabelOrValueAsLabel } from '../utils/generics';
export type SingleSelectionChoiceProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> = {
  type: ChartDimensionChoice<DataKey, DataRow>['type'];
  choice: ChartDimensionChoice<DataKey, DataRow>;
  selected: SingleSelectionValue<string>;
  otherSelected?: never;
} | {
  type: ChartMetricChoice<DataKey, DataRow>['type'];
  choice: ChartMetricChoice<DataKey, DataRow>;
  selected: SingleSelectionValue<string>;
  otherSelected?: never;
} | {
  type: ChartMetricVariantsChoice<DataKey, DataRow>['type'];
  choice: ChartMetricVariantsChoice<DataKey, DataRow>;
  selected: SingleSelectionValue<string>;
  otherSelected?: never;
} | {
  type: ChartMetricTransposedChoice<DataKey, DataRow>['type'];
  choice: ChartMetricTransposedChoice<DataKey, DataRow>;
  selected: SingleSelectionValue<string>;
  otherSelected?: never;
} | {
  type: ChartVariantValueChoice<DataKey, DataRow>['type'];
  choice: ChartVariantValueChoice<DataKey, DataRow>;
  selected: SingleSelectionValue<string>;
  otherSelected?: never;
} | {
  type: ChartDimensionValueChoice<DataKey, DataRow>['type'];
  choice: ChartDimensionValueChoice<DataKey, DataRow>;
  selected: SingleSelectionValue<string>;
  otherSelected?: never;
} | {
  type: ChartDimensionValueOfChoice<DataKey, DataRow>['type'];
  choice: ChartDimensionValueOfChoice<DataKey, DataRow>;
  selected: SingleSelectionValue<string>;
  otherSelected?: SingleSelectionValue<DataKey>;
};
type SingleSelectionProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> = SingleSelectionChoiceProps<DataKey, DataRow> & {
  buttonWidth?: string;
  dispatch: Dispatch<ChartCardStateAction<DataKey, DataRow>>;
  showBadge?: boolean;
  locked?: boolean;
};
const SingleSelection = props => {
  const $ = _c(37);
  const {
    choice,
    selected,
    dispatch,
    buttonWidth,
    showBadge
  } = props;
  const locked = props.locked === true;
  const {
    id,
    type,
    label,
    description: choiceDescription,
    color: choiceColor
  } = choice;
  const description = choice.change.description ?? choiceDescription;
  let options;
  if (type === "valueOf") {
    const {
      otherSelected
    } = props;
    options = choice.change.options[otherSelected as DataKey];
  } else {
    options = choice.change.options;
  }
  let t0;
  if ($[0] !== options || $[1] !== showBadge) {
    let t1;
    if ($[3] !== showBadge) {
      t1 = t2 => {
        const {
          value,
          label: label_0,
          description: description_0,
          color
        } = t2;
        return {
          value,
          label: getLabelOrValueAsLabel(label_0, value),
          placement: "bottom-end",
          tooltipProps: description_0 == null || description_0 === label_0 ? undefined : {
            kind: "singleline",
            title: description_0
          },
          icon: color == null || showBadge !== true ? undefined : <Badge color={color} variant="lighter" />
        };
      };
      $[3] = showBadge;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = options.map(t1);
    $[0] = options;
    $[1] = showBadge;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const items = t0;
  if (!changeIsVisible(choice)) {
    return null;
  }
  let t1;
  if ($[5] !== description || $[6] !== label) {
    t1 = description != null && description !== label ? {
      kind: "singleline",
      title: description,
      placement: "bottom-end"
    } : undefined;
    $[5] = description;
    $[6] = label;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const tooltipProps = t1;
  let t2;
  if ($[8] !== choice.change || $[9] !== dispatch || $[10] !== id || $[11] !== props.otherSelected) {
    t2 = value_0 => {
      let item = value_0;
      const {
        change: changeType,
        requestChange,
        onChange
      } = choice.change;
      bb38: switch (changeType) {
        case "static":
        case "controlled":
          {
            return;
          }
        case "hybrid":
          {
            item = requestChange(value_0 as DataKey, id, props.otherSelected);
            if (item == null) {
              return;
            }
            break bb38;
          }
        case "internal":
          {
            break bb38;
          }
        default:
          {
            assertNever(changeType);
            return;
          }
      }
      dispatch({
        type: "singleSet",
        id,
        item
      });
      if (onChange != null) {
        onChange(item as DataKey, id, props.otherSelected);
      }
    };
    $[8] = choice.change;
    $[9] = dispatch;
    $[10] = id;
    $[11] = props.otherSelected;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const onSelect = t2;
  if (!locked) {
    const t3 = items.length > 10;
    const t4 = description ?? label ?? id;
    const t5 = buttonWidth ?? "200px";
    let t6;
    if ($[13] !== id || $[14] !== items || $[15] !== locked || $[16] !== onSelect || $[17] !== selected || $[18] !== t3 || $[19] !== t4 || $[20] !== t5 || $[21] !== tooltipProps) {
      t6 = <SingleSelectDropdown key={id} includeSearch={t3} title={t4} buttonWidth={t5} tooltipProps={tooltipProps} value={selected} options={items} onSelect={onSelect} disabled={locked} />;
      $[13] = id;
      $[14] = items;
      $[15] = locked;
      $[16] = onSelect;
      $[17] = selected;
      $[18] = t3;
      $[19] = t4;
      $[20] = t5;
      $[21] = tooltipProps;
      $[22] = t6;
    } else {
      t6 = $[22];
    }
    return t6;
  }
  let t3;
  if ($[23] !== options || $[24] !== selected) {
    t3 = options.find(t4 => {
      const {
        value: value_1
      } = t4;
      return value_1 === selected;
    }) ?? {};
    $[23] = options;
    $[24] = selected;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  const {
    label: selectedLabel,
    description: selectedDescription,
    color: selectedColor
  } = t3;
  const badgeColor = selectedColor ?? choiceColor;
  const t4 = selectedLabel ?? selected;
  let t5;
  if ($[26] !== badgeColor || $[27] !== showBadge) {
    t5 = badgeColor == null || showBadge !== true ? undefined : {
      color: badgeColor,
      variant: "lighter"
    };
    $[26] = badgeColor;
    $[27] = showBadge;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  let t6;
  if ($[29] !== id || $[30] !== t4 || $[31] !== t5) {
    t6 = <Chip key={id} label={t4} badgeProps={t5} />;
    $[29] = id;
    $[30] = t4;
    $[31] = t5;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  const chip = t6;
  if (selectedDescription == null) {
    return chip;
  }
  let t7;
  if ($[33] !== chip || $[34] !== id || $[35] !== selectedDescription) {
    t7 = <Tooltip key={id} kind="singleline" title={selectedDescription} placement="bottom-end">{chip}</Tooltip>;
    $[33] = chip;
    $[34] = id;
    $[35] = selectedDescription;
    $[36] = t7;
  } else {
    t7 = $[36];
  }
  return t7;
};
export default SingleSelection;